import {
	JSX,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";

export type ListRowInfo = {
	visible: boolean;
	enabled: boolean;
	selected: boolean;
};

export type ListInfoMap = {
	[key: string]: ListRowInfo;
};

export type ListInfo = {
	elements: ListInfoMap;

	setSelected: (keys: Array<string>, selected: boolean) => void;
};

const ListInfoContext = createContext<ListInfo>({
	elements: {},

	setSelected: () => {}
});

export const useListInfo = (): ListInfo => useContext(ListInfoContext);

export type ListInfoProviderProps = {
	keys: Array<string>;
	isEnabled?: (key: string) => boolean;

	children: React.ReactNode;
};

export default function ListInfoProvider(
	props: Readonly<ListInfoProviderProps>
): JSX.Element {
	const [items, setItems] = useState<ListInfoMap>({});

	const updateItems = useCallback(
		(
			elementKeys: Array<string>,
			mapper: (value: ListRowInfo) => ListRowInfo
		): void => {
			setItems(v => {
				const updated: ListInfoMap = {};
				for (const key of elementKeys) {
					updated[key] = mapper(v[key]);
				}

				return {
					...v,
					...updated
				};
			});
		},
		[]
	);

	useEffect(() => {
		setItems(v => {
			const updated: ListInfoMap = {};
			for (const key of props.keys) {
				updated[key] = v[key] ?? {
					visible: true,
					enabled: props.isEnabled?.(key) ?? true,
					selected: false
				};
			}

			return updated;
		});
	}, [props.keys]);

	useEffect(() => {
		setItems(v => {
			const updated: ListInfoMap = {};
			for (const entry of Object.entries(v)) {
				updated[entry[0]] = {
					...entry[1],
					enabled: props.isEnabled?.(entry[0]) ?? true
				};
			}

			return updated;
		});
	}, [props.isEnabled]);

	const setSelected = useCallback(
		(elements: Array<string>, selected: boolean): void => {
			updateItems(elements, value => ({
				...value,
				selected
			}));
		},
		[updateItems]
	);

	const context: ListInfo = useMemo(
		() => ({
			elements: items,

			setSelected
		}),
		[items]
	);

	return (
		<ListInfoContext.Provider value={context}>
			{props.children}
		</ListInfoContext.Provider>
	);
}
